import { useCallback, useEffect, useRef, useState } from 'react';
import { useRuntime } from './context';
import { actionButtonName, actionIconOf } from './brand';
import type { ActionIcon } from './brand';
import { useT } from '../messages';
import type { Session, StaffBranding } from '../wire';

/**
 * The **online** read, and the seam between it and everything else in this app.
 *
 * `useLive` (in `context.tsx`) is the offline rule made into a hook: a view
 * calls it, it reads IndexedDB, and there is deliberately no way for a view to
 * reach the network. That rule holds for the book, and this hook does not break
 * it — it is the rule for a *different kind of screen*, stated once here so no
 * individual screen has to argue it again.
 *
 * ## Why the vertical screens are online
 *
 * Spec §8's offline design is about **the book**: the appointments a counter
 * works from, which must survive a tunnel because the alternative is a shop that
 * stops selling. A kitchen pass (§3.6), a dock (§4.8) and a front desk (§4.7)
 * are not that. Three things make them different, and each of them is a reason
 * not to mirror:
 *
 *  1. **Their state is a race with other people.** An order lane is the truth
 *     about a bag of food that a runner may have just collected; a manifest is a
 *     boat somebody may have just cancelled. Showing a cached lane is not
 *     staleness, it is a wrong answer to "is this ready".
 *  2. **The buttons come from the server.** `next_statuses` is `ORDER_TRANSITIONS`
 *     projected onto one row (`orderToWire`). Cached with the row it would draw
 *     a button the domain now refuses, and the whole reason the server sends it
 *     is so a stale tablet does not do that.
 *  3. **Nothing here is queueable.** A weather call cascades forty cancellations
 *     and notifies a partner; a stay cancellation returns a fee the *server*
 *     computed. Neither is a thing to hold in an outbox and replay against a
 *     world that moved — §8.3's whole design is that a conflict becomes an
 *     owner's task, and these verbs have no sensible task form.
 *
 * So: fetch, show what was fetched, and **stamp when**. The freshness line is
 * the same honesty §8.1 asks of the mirror, applied to a screen that has no
 * mirror to be honest about.
 *
 * ## SSE first, poll as the backstop
 *
 * The app already holds one authenticated event stream (`mirror/sync.ts`), and
 * `order.created` / `order.updated` / `order.ready` ride it — they are ordinary
 * outbox rows and `routes/events.ts` forwards whatever is in the table. So a cue
 * arrives in about a second, and `runtime.subscribeEvents` is how a screen with
 * no mirror rows gets it. The poll stays anyway, at a deliberately unhurried
 * interval: the stream can be down while the screen is perfectly reachable
 * (that is what `connection()` says), and a pass that stopped updating because a
 * proxy ate a stream would be the worst of both designs.
 */

export interface Remote<T> {
  /** The last successful answer, kept across a failing reload. */
  data: T | null;
  error: Error | null;
  /** True only while a request is in flight with nothing to show yet. */
  loading: boolean;
  /** `runtime.now()` at the moment the answer landed. */
  fetchedAt: number | null;
  reload: () => void;
}

export interface RemoteOptions {
  /** The backstop. `0` disables it. */
  pollMs?: number;
  /** Which server events should trigger an immediate re-read. */
  cue?: (type: string, subjectId: string | null) => boolean;
  /** `false` keeps the hook idle — a module that is off asks nothing. */
  enabled?: boolean;
}

const DEFAULT_POLL_MS = 15_000;

export function useRemote<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: RemoteOptions = {},
): Remote<T> {
  const runtime = useRuntime();
  const { pollMs = DEFAULT_POLL_MS, cue, enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);

  // The reads and the predicate change identity on every render (they close over
  // props); the *effect* must not. Refs keep the latest without making the
  // subscription churn — the same shape `useLive` uses, for the same reason.
  const loadRef = useRef(load);
  loadRef.current = load;
  const cueRef = useRef(cue);
  cueRef.current = cue;

  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let alive = true;
    let controller: AbortController | null = null;

    const run = (): void => {
      controller?.abort();
      const own = new AbortController();
      controller = own;
      void loadRef
        .current(own.signal)
        .then((next) => {
          if (!alive || own.signal.aborted) return;
          setData(next);
          setError(null);
          setFetchedAt(runtime.now());
        })
        .catch((e: unknown) => {
          if (!alive || own.signal.aborted) return;
          // The previous answer stays on screen beside the failure. A pass that
          // blanked itself because one poll missed would be less useful than one
          // that says "this is from 4:12 and I could not reach the server since".
          setError(e instanceof Error ? e : new Error(String(e)));
        })
        .finally(() => {
          if (alive && !own.signal.aborted) setLoading(false);
        });
    };

    run();

    const timer =
      pollMs > 0 ? setInterval(run, pollMs) : (null as ReturnType<typeof setInterval> | null);

    const unsubscribe = runtime.subscribeEvents((type, subjectId) => {
      if (cueRef.current?.(type, subjectId) === true) run();
    });

    return () => {
      alive = false;
      controller?.abort();
      if (timer !== null) clearInterval(timer);
      unsubscribe();
    };
    // `deps` is spread rather than closed over: the caller names what makes the
    // read a *different* read (the day, the order id), the refs carry the rest.
  }, [runtime, enabled, pollMs, nonce, ...deps]);

  return { data, error, loading, fetchedAt, reload };
}

/* ------------------------------------------------------------------ */
/* Module gating                                                       */
/* ------------------------------------------------------------------ */

/**
 * The tenant's modules, out of the mirrored `GET /v1/session`.
 *
 * `modules` there is spec §3.1's table crossed with what the tenant bought, and
 * it is the same list `capabilitiesFor` projects on the partner surface —
 * **never** a client-side guess and never a route table. A screen whose module
 * is off is *hidden*: the tab is not drawn, so nothing 404s and nobody is shown
 * a door that does not open. §3.2's own argument for `404` over `501` is the
 * same one — we do not advertise what a tenant has not bought.
 */
export function useModules(): string[] {
  const runtime = useRuntime();
  // Seeded from the mirror's in-memory snapshot, so a device that has already
  // hydrated draws the right tabs on its *first* paint rather than one
  // IndexedDB round trip later. The effect below still runs and still corrects
  // it; on a cold open the snapshot is `undefined` and this is `[]`, exactly as
  // it was before.
  const [modules, setModules] = useState<string[]>(
    () => runtime.mirror.sessionNow()?.modules ?? [],
  );
  useEffect(() => {
    let alive = true;
    const read = (): void => {
      void runtime.mirror
        .session()
        .then((session: Session | null) => {
          if (alive) setModules(session?.modules ?? []);
        })
        .catch(() => undefined);
    };
    read();
    const unsubscribe = runtime.subscribe(read);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [runtime]);
  return modules;
}

export function useHasModule(module: string): boolean {
  return useModules().includes(module);
}

/* ------------------------------------------------------------------ */
/* The named set (docs/lovespa-personality.md)                         */
/* ------------------------------------------------------------------ */

/**
 * The tenant's staff branding, out of the same mirrored `GET /v1/session`.
 *
 * Deliberately the twin of `useModules` — same read, same subscription, same
 * argument. The theme is *not* a network fetch and must not become one: the
 * session is the first thing `mirror.ts` writes, so this resolves in a few
 * milliseconds on a cold open and keeps resolving in a tunnel. A counter app
 * that lost the business's colours when the radio dropped would be the same
 * class of dishonesty §8.1 spends a status strip preventing.
 *
 * `undefined` — the value for a tenant with no branding, which is all of them
 * but one — is what makes the app render today's DOM byte for byte.
 */
export function useStaffBranding(): StaffBranding | undefined {
  const runtime = useRuntime();
  // Same seeding as `useModules`, and here it is load-bearing rather than
  // cosmetic: the empty states whose copy follows the icon choice would
  // otherwise paint the unbranded sentence first and the branded one a tick
  // later, which is a visible flicker on a counter and a race in a test.
  const [branding, setBranding] = useState<StaffBranding | undefined>(
    () => runtime.mirror.sessionNow()?.branding_staff,
  );
  useEffect(() => {
    let alive = true;
    const read = (): void => {
      void runtime.mirror
        .session()
        .then((session: Session | null) => {
          if (alive) setBranding(session?.branding_staff);
        })
        .catch(() => undefined);
    };
    read();
    const unsubscribe = runtime.subscribe(read);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [runtime]);
  return branding;
}

/**
 * Which glyph the add/walk-in/issue affordances wear, and what the empty states
 * call it.
 *
 * One hook rather than two reads, because the icon and the copy must never
 * disagree — the personality doc's inventory names that pairing explicitly
 * ("copy follows the icon choice automatically"), and it is the detail most
 * likely to rot: somebody adds an empty state, writes "the + button", and a
 * gold-heart tenant is told to press something that is not on their screen.
 */
export function useActionIcon(): { icon: ActionIcon; buttonName: string } {
  const t = useT();
  const icon = actionIconOf(useStaffBranding());
  return { icon, buttonName: actionButtonName(icon, t) };
}
